
<script>
import dropdownIcon from '@/assets/images/icons/arrow-dropdown.svg?inline'
import removeIcon from '@/assets/images/icons/close.svg?inline'
import { nToWord } from '@/utils/helpers'

export default {
  name: 'RexSelect',
  components: {
    dropdownIcon,
    removeIcon
  },
  props: {
    name: {
      type: String,
      required: true
    },
    storeModule: {
      type: String,
      default: () => null,
      required: true
    },
    getter: {
      type: String,
      default: () => 'getField'
    },
    setter: {
      type: String,
      default: () => 'setField'
    },
    target: {
      type: String,
      default: () => null,
      required: true
    },
    arraySelectIndex: {
      type: [String, Number],
      default: () => 0
    },
    position: {
      type: String,
      default: () => null
    },
    validate: {
      type: [String, Object],
      default: () => null
    },
    options: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: () => null
    },
    emptyLabel: {
      type: String,
      default: () => 'Не выбрано'
    },
    placeholder: {
      type: String,
      default: () => null
    },
    hideIfEmpty: {
      type: Boolean,
      default: () => true
    },
    hasCustomTrigger: {
      type: Boolean,
      default: () => false
    },
    hasBackendValidation: {
      type: Boolean,
      default: () => false
    },
    hasSearch: {
      type: Boolean,
      default: () => false
    },
    isDisabled: {
      type: Boolean,
      default: () => false
    },
    isGrouped: {
      type: Boolean,
      default: () => false
    },
    isMultiple: {
      type: Boolean,
      default: () => false
    },
    isHoverable: {
      type: Boolean,
      default: () => false
    },
    isTagged: {
      type: Boolean,
      default: () => false
    },
    isArraySelect: {
      type: Boolean,
      default: () => false
    },
  },
  data: () => ({
    active: false,
    searchQuery: ''
  }),
  inject: ['$validator'],
  computed: {
    value: {
      get() {
        let value = this.$store.getters[`${this.storeModule}/${this.getter}`](this.target)

        if (this.isArraySelect && value) {
          value = value[`${this.arraySelectIndex}`]
        }

        return value
      },
      set(value) {
        let data = null

        if (this.isArraySelect) {
          const arr = this.$store.getters[`${this.storeModule}/${this.getter}`](this.target)
          arr[`${this.arraySelectIndex}`] = value

          data = arr
        } else {
          data = value
        }

        this.$store.commit(`${this.storeModule}/${this.setter}`, {
          target: this.target,
          value: data
        })

        if (this.error !== null && this.hasBackendValidation) {
          const target = this.target.split('.')
          const err = this.$store.getters[`${this.storeModule}/${this.getter}`](`${target[0]}Errors`)
          const currentFieldErrorIndex = err.map(x => x.source).indexOf(this.name)

          err.splice(currentFieldErrorIndex, 1)

          this.$store.commit(`${this.storeModule}/${this.setter}`, err)
        }

        this.$emit('change', {
          element: this.$vnode.elm,
          event: data
        })
      }
    },
    error() {
      let error = null

      if (this.hasBackendValidation) {
        const target = this.target.split('.')
        const err = this.$store.getters[`${this.storeModule}/${this.getter}`](`${target[0]}Errors`)
          .find(item => item.source === this.name)

        error = err ? err.detail : null
      }

      return error
    },
    ungroupedOptions() {
      let options = this.options

      if (this.isGrouped) {
        options = [].concat(...this.options.map(item => item.collection))
      }

      return options
    },
    selectedLabel() {
      let label = this.emptyLabel

      if (this.isMultiple && this.value.length) {
        label = `${this.value.length} ${nToWord(this.value.length, ['вариант', 'варианта', 'вариантов'])}`
      } else {
        const selectedItem = this.ungroupedOptions.find(item => item.slug === this.value)

        if (selectedItem) {
          label = selectedItem.name
        }
      }

      return label
    },
    tags() {
      return this.value.map(item => ({
        name: this.ungroupedOptions.find(option => option.slug === item).name,
        slug: item,
      }))
    },
    filteredOptions() {
      let options = this.options

      if (this.hasSearch && this.searchQuery.length >= 2) {
        options.sort(
          (prev, next) => prev.name.indexOf(this.searchQuery) - next.name.indexOf(this.searchQuery)
        )
        options = options.filter(item => item.name.indexOf(this.searchQuery) > -1)
      }

      return options
    },
    isFieldEmpty() {
      return !(this.isMultiple
        ? this.value.length
        : this.value)
    }
  },
  mounted() {
    if ((this.isTagged || this.hasSearch) && this.$refs.dropdown) {
      this.$refs.dropdown.toggle = () => {
        this.$refs.dropdown.isActive = true
      }
    }
  },
  methods: {
    toggleFocus(active) {
      this.active = active
    },
    removeSelected(slug) {
      this.$refs.dropdown.toggle()

      this.value = this.value.filter(item => !(item === slug))
    }
  }
}
</script>

<template>
  <b-field
    custom-class="rex-label"
    :label="label"
    :type="{'is-danger': errors.has(name) || error}"
    :message="errors.first(name) || error"
  >
    <b-dropdown
      v-if="hideIfEmpty ? options.length : true"
      ref="dropdown"
      v-model="value"
      v-validate="validate"
      :name="name"
      class="rex-select"
      :class="{
        'rex-select--multiple': isMultiple,
        'rex-select--empty': isFieldEmpty,
        'rex-select--has-error': errors.first(name) || error,
      }"
      :position="position"
      :disabled="isDisabled"
      :multiple="isMultiple"
      :hoverable="isHoverable"
      @active-change="toggleFocus"
    >
      <div
        slot="trigger"
        class="rex-select__trigger-wrap"
      >
        <slot
          v-if="hasCustomTrigger"
        />
        <div
          v-else
          class="rex-select__trigger"
          :class="{
            'rex-select__trigger--collapse-padding': hasSearch
          }"
        >
          <div
            v-if="hasSearch"
            class="rex-select__trigger-search"
          >
            <input
              v-model="searchQuery"
              :placeholder="placeholder"
              type="text"
            >
          </div>
          <div
            v-else-if="isTagged && tags.length"
            class="rex-select__trigger-tags"
          >
            <div class="rex-select__trigger-tags-item">
              <span>{{ tags[0].name }}</span>
              <button
                @click="removeSelected(tags[0].slug)"
              >
                <remove-icon />
              </button>
            </div>
            <div
              v-if="tags.length - 1"
              class="rex-select__trigger-tags-count"
            >
              {{ `+${tags.length - 1}` }}
            </div>
          </div>
          <span v-else>
            {{ selectedLabel }}
          </span>
          <dropdown-icon
            class="rex-select__trigger-icon"
            :class="{
              'rex-select__trigger-icon--active': !isFieldEmpty
            }"
          />
        </div>
      </div>
      <div v-if="isGrouped">
        <div
          v-for="(group, index) in options"
          :key="index"
        >
          <b-dropdown-item
            :custom="true"
            class="rex-select__group-label"
            :disabled="group.disabled"
          >
            <div class="rex-select__option">
              <span>{{ group.label }}</span>
              <span
                v-if="group.badge"
                class="rex-select__option-badge"
              >
                {{ group.badge }}
              </span>
            </div>
          </b-dropdown-item>
          <b-dropdown-item
            v-for="option in group.collection"
            :key="option.slug"
            :value="option.slug"
            :disabled="option.disabled || group.disabled"
          >
            <div class="rex-select__option">
              <span>{{ option.name }}</span>
              <span
                v-if="option.badge"
                class="rex-select__option-badge"
              >
                {{ option.badge }}
              </span>
            </div>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="index !== options.length - 1"
            separator
          />
        </div>
      </div>
      <div v-else>
        <b-dropdown-item
          v-if="!filteredOptions.length"
          :custom="true"
          :disabled="true"
        >
          Ничего не найдено
        </b-dropdown-item>
        <div v-else>
          <b-dropdown-item
            v-for="option in filteredOptions"
            :key="option.slug"
            :value="option.slug"
            :disabled="option.disabled"
          >
            <div class="rex-select__option">
              <span>{{ option.name }}</span>
              <span
                v-if="option.badge"
                class="rex-select__option-badge"
              >
                {{ option.badge }}
              </span>
            </div>
          </b-dropdown-item>
        </div>
      </div>
    </b-dropdown>
    <div
      v-if="isTagged && hasSearch && tags.length"
      class="rex-select__tags"
      :class="{
        'rex-select__tags--disabled': isDisabled
      }"
    >
      <div
        v-for="tag in tags"
        :key="tag.slug"
        class="rex-select__trigger-tags-item"
      >
        <span>{{ tag.name }}</span>
        <button
          :disabled="isDisabled"
          @click="removeSelected(tag.slug)"
        >
          <remove-icon />
        </button>
      </div>
    </div>
  </b-field>
</template>